import { StationeryPaperSize } from '@apps/card/routes/CardCustomizer/steps/CardDesign/config';
import { useTranslation } from '@shared/core';
import type { Product } from './product.types';
import { useMemo } from 'react';

const A2_ENVELOPE_SKU = 'WJYOEA2W';
const A7_ENVELOPE_SKU = 'WJYOEA7W';
// A6 (magnets). Unlike A2/A7, Taylor issued one item number per print mode
// (Kaila, #joy-taylor 2026-10-02), so the SKU itself says whether and how the
// envelope is printed — the asset type alone is not enough on this line.
const A6_ENVELOPE_SKUS = {
  blank: 'WJYOEA6WB', // A6 White Env, Blank
  returnAddress: 'WJYOEA6WRAB', // A6 White Env, Return Address Back Flap
  returnAddressVariableRecipient: 'WJYOEA6WRABVFP', // A6 White Env, Return Address Back Flap, Variable Front
} as const;

export const useProductTypes = (): Product[] => {
  const { tq } = useTranslation('stationery');

  return useMemo(
    () => [
      {
        type: 'stationeryCard',
        mappingFunction: 'taylormap',
        supportedQuantities: new Array(40).fill(null).map((_, index) => (index + 1) * 10),
        cardTypeSKU: {
          [StationeryPaperSize.A2]: {
            envelopeSKU: A2_ENVELOPE_SKU,
            cardType: 'FlatNoteCard',
          },
          [StationeryPaperSize.A7]: {
            envelopeSKU: A7_ENVELOPE_SKU,
            cardType: 'FlatNoteCard',
          },
          // Postcards (4x6/6x4) are self-mailers: no envelopeSKU, ever.
          // Before postcard4x6 existed they fell through to the A7 key and
          // could pick up its envelope SKU when the envelope step was on.
          [StationeryPaperSize.postcard4x6]: {
            cardType: 'FlatNoteCard',
          },
        },
        translations: {
          displayName: tq`Classic Paper`,
          formatName: tq`Paper`,
          galleryPriceStringName: tq`Paper`,
        },
      },
      {
        // Save-the-date magnets: a separate product on the same 4x6/6x4
        // saveTheDate templates as the postcard product (card_service
        // MAGNET4X6). cardType 'Magnet' routes single-surface fulfillment.
        type: 'magnet',
        defaultQuantity: 100,
        mappingFunction: 'taylormap',
        // Approved spec: minimum quantity 4, then tens up to 400.
        supportedQuantities: [4, ...new Array(40).fill(null).map((_, index) => (index + 1) * 10)],
        cardTypeSKU: {
          [StationeryPaperSize.postcard4x6]: {
            cardType: 'Magnet',
            envelopeSKUByAddressing: A6_ENVELOPE_SKUS,
          },
        },
        translations: {
          displayName: tq`Magnets`,
          formatName: tq`Magnet 4x6`,
          galleryPriceStringName: tq`Magnets`,
        },
      },
      {
        // Standalone envelopes — return and/or recipient addressing sold on their own,
        // with no card. `cardType: 'Envelope'` routes card_service's envelope-only Taylor
        // mapper; the blank-envelope SKU is the product's own `vendorBaseSKU`, so there is
        // deliberately no `envelopeSKU` here — that field is for the envelope that rides
        // along with a card order, and this product IS the envelope.
        //
        // A7 only at launch: the envelope canvas is hardcoded 7.25 x 5.25 in Envelope.tsx,
        // EnvelopeFace.tsx and envelopeTemplateUtils.ts, and `getStationeryPaperSize` falls
        // through to A7 for the carrier template's category.
        type: 'envelope',
        defaultQuantity: 100,
        mappingFunction: 'taylormap',
        // Approved pricing: flat $0.50 each, minimum 20, then tens to 400. The price itself
        // lives in the backend price config — never mirror it here. No leading literal the way
        // magnet needs one for 4: 20 already sits on the ten-grid, so (index + 2) * 10 covers
        // the whole ladder, 20 through 400.
        supportedQuantities: new Array(39).fill(null).map((_, index) => (index + 2) * 10),
        cardTypeSKU: {
          [StationeryPaperSize.A7]: {
            cardType: 'Envelope',
          },
        },
        translations: {
          displayName: tq`Envelopes`,
          formatName: tq`Envelopes`,
          galleryPriceStringName: tq`Envelopes`,
        },
      },
      {
        type: 'digitalCard',
        defaultQuantity: 250,
        mappingFunction: 'joydigitalmap',
        supportedQuantities: [50, 100, 250, 500, 1000],
        translations: {
          displayName: tq`Premium Digital`,
          formatName: tq`Premium Digital Cards`,
          galleryPriceStringName: tq`Digital`,
        },
      },
      {
        type: 'napkin',
        defaultQuantity: 100,
        mappingFunction: 'taylormap',
        supportedQuantities: [50, 100, 150, 200, 250, 300, 350, 400, 450, 500],
        cardTypeSKU: {
          [StationeryPaperSize.napkin475]: {
            cardType: 'FlatNoteCard',
          },
          [StationeryPaperSize.napkin650]: {
            cardType: 'FlatNoteCard',
          },
        },
        translations: {
          displayName: tq`Napkins`,
          formatName: tq`Napkin`,
          galleryPriceStringName: tq`Napkins`,
        },
      },
      {
        type: 'sign',
        defaultQuantity: 100,
        mappingFunction: 'taylormap',
        supportedQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 50],
        cardTypeSKU: {
          [StationeryPaperSize.foam18X24]: {
            cardType: 'FlatNoteCard',
          },
          [StationeryPaperSize.foam24X36]: {
            cardType: 'FlatNoteCard',
          },
        },
        translations: {
          displayName: tq`Signs`,
          formatName: tq`Foam Board Signs`,
          galleryPriceStringName: tq`Signs`,
        },
      },
      {
        type: 'digital',
        supportedQuantities: [1],
        mappingFunction: 'freedigitalmap',
        translations: {
          displayName: tq`Basic Digital`,
          formatName: tq`Basic Digital`,
          // Matches the premium-digital label so digital-only tiles read "Digital Free"
          // (with a colored "Free"), consistent with paper+digital tiles.
          galleryPriceStringName: tq`Digital`,
        },
      },
      {
        type: 'basicDigitalV2',
        supportedQuantities: [1],
        mappingFunction: 'freedigitalmap',
        translations: {
          displayName: tq`Basic Digital`,
          formatName: tq`Basic Digital`,
          galleryPriceStringName: tq`Digital`,
        },
      },
      {
        type: 'sampleKit',
        supportedQuantities: [1],
        mappingFunction: 'taylormap',
        translations: {
          displayName: tq`Sample Kit`,
          formatName: tq`Sample Kit`,
          galleryPriceStringName: tq`Sample Kit`,
        },
      },
    ],
    [tq],
  );
};
